import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Thermometer } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { useApi, fmtDT } from "@/lib/api";
import { brandHex } from "@/lib/brandColor";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stat, Card, Pill } from "@/components/kit";
import VenueComparison from "@/components/VenueComparison";
import { HygieneRating } from "@/components/HygieneRating";
import { MyActions } from "@/components/ActionPlan";

function ScoreRing({ score }) {
  const tone = score >= 85 ? brandHex() : score >= 60 ? "#D97706" : "#E11D48";
  const c = 2 * Math.PI * 52;
  return (
    <div className="relative h-36 w-36">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r="52" stroke="#E2E8F0" strokeWidth="10" fill="none" />
        <circle cx="60" cy="60" r="52" stroke={tone} strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span data-testid="compliance-score-badge" className="font-display text-4xl font-extrabold text-slate-900">{score}</span>
        <span className="ka-eyebrow">score</span>
      </div>
    </div>
  );
}

function Exceptions({ items }) {
  if (!items.length) {
    return <div data-testid="exception-banner-clear" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800"><CheckCircle2 className="h-5 w-5" />No exceptions right now. Nice work.</div>;
  }
  return (
    <div data-testid="exception-banner-alert" className="rounded-2xl border border-rose-200 bg-rose-50/70 p-5">
      <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-rose-800"><AlertTriangle className="h-5 w-5" />{items.length} thing{items.length > 1 ? "s need" : " needs"} attention</div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {items.map((e, i) => <li key={i} className="flex items-center gap-2 text-sm text-rose-900"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" />{e.text}</li>)}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const { venueId, venue, user, can } = useAuth();
  const { data } = useApi(venueId ? "/dashboard" : null, { venue_id: venueId });
  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-slate-200/60" />;
  const h = new Date().getHours();
  const greet = h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
  return (
    <div className="space-y-8">
      <PageHeader eyebrow={venue?.name} title={`${greet}, ${user.name.split(" ")[0]}`} subtitle="Here's how food safety looks across this venue today." />
      <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
        <Card className="flex flex-wrap items-center gap-6"><ScoreRing score={data.score} /><div><div className="ka-eyebrow">Checks today</div><div data-testid="checks-progress" className="font-display text-3xl font-extrabold">{data.checks_done}/{data.checks_total}</div><Link to="/app/checks" data-testid="dashboard-goto-checks" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">Open checks <ArrowRight className="h-3.5 w-3.5" /></Link></div><div className="w-full max-w-xs"><HygieneRating data={data.inspector_rating} linked={can("admin")} /></div></Card>
        <Exceptions items={data.exceptions} />
      </div>
      <VenueComparison />
      <MyActions venueId={venueId} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Overdue checks" value={data.overdue.length} tone={data.overdue.length ? "rose" : "emerald"} testid="stat-overdue" />
        <Stat label="Open incidents" value={data.open_incidents} tone={data.open_incidents ? "amber" : "emerald"} testid="stat-incidents" />
        <Stat label="Out of range (7d)" value={data.out_of_range_7d} tone={data.out_of_range_7d ? "rose" : "emerald"} sub={`${data.readings_7d} readings`} testid="stat-oor" />
        <Stat label="Training expiring" value={data.training_expiring + data.training_expired} tone={data.training_expired ? "rose" : data.training_expiring ? "amber" : "emerald"} sub="next 30 days / expired" testid="stat-training" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold">Temperature readings · 7 days</h3><Link to="/app/temps" className="text-sm font-semibold text-emerald-700 hover:underline">Log a reading</Link></div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart}><XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={12} /><YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} width={28} /><Tooltip cursor={{ fill: "#F1F5F9" }} /><Bar dataKey="readings" stackId="a" fill={brandHex()} radius={[0, 0, 0, 0]} name="In range" /><Bar dataKey="out_of_range" stackId="a" fill="#E11D48" radius={[6, 6, 0, 0]} name="Out of range" /></BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="mb-4 text-lg font-bold">Equipment · latest reading</h3>
          <ul className="space-y-3">
            {data.equipment_latest.map((e) => (
              <li key={e.id} data-testid={`equipment-latest-${e.id}`} className="flex items-center gap-3">
                <Thermometer className="h-4 w-4 text-slate-400" />
                <span className="flex-1 text-sm font-medium text-slate-800">{e.name}</span>
                {e.latest ? <Pill status={e.latest.out_of_range ? "overdue" : "ok"} label={`${e.latest.value}°C`} /> : <span className="text-xs text-slate-400">No reading 7d</span>}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card>
        <h3 className="mb-4 text-lg font-bold">Recent activity</h3>
        <ul className="divide-y divide-slate-100">
          {data.recent.map((a) => <li key={a.id} className="flex flex-wrap justify-between gap-2 py-2.5 text-sm"><span className="text-slate-700"><b className="text-slate-900">{a.user_name}</b> · {a.summary}</span><span className="font-mono text-xs text-slate-400">{fmtDT(a.at)}</span></li>)}
          {!data.recent.length && <li className="text-sm text-slate-500">No activity yet.</li>}
        </ul>
      </Card>
    </div>
  );
}
